/**
 * JobListTemplate - Shared dashboard template for /work-orders and /inspections pages.
 *
 * Provides a unified layout for listing job-type records with:
 *	 - Page header with title, subtitle, and create button
 *	 - Stat summary cards row (configurable per entity type)
 *	 - Data table with right-side filter panel (desktop) / bottom sheet (mobile)
 *	 - Pagination with page numbers and showing X of Y entries
 *	 - Loading skeletons, empty state, and error state
 *
 * The template is fully configurable via props: stat cards, table columns,
 * filter controls, and API fetch function are all injected by the consuming page.
 *
 * This avoids duplicating ~500 lines of nearly identical layout/pagination/filter
 * logic between WorkOrdersPage and InspectionsPage.
 */
import React, { useState } from 'react';
import { useIsDesktop } from '../../../hooks/useMediaQuery';
import { dedupeAndSort } from '../../../utils/dedupeAndSort';
import { useNavigate } from 'react-router-dom';
import { Checkbox } from '../../atoms/Checkbox';
import { Button } from '../../atoms/Button';
import { PageHeader } from '../../molecules/PageHeader';
import { JobListMain } from '../../organisms/JobListMain';
import { Modal } from '../../organisms/Modal';
import type { ColumnConfig } from '../../organisms/JobListMain';

/* ============================================
 * TYPE DEFINITIONS
 * ============================================ */

/** Configuration for a single stat summary card */
export interface StatCardConfig {
	/** Label displayed above the number (e.g., "OPEN ORDERS") */
	label: string;
	/** The value to display (number or placeholder string) */
	value: string | number;
	/** Subtitle text below the number */
	subtitle: string;
	/** Whether this card uses the accent background (first card pattern) */
	isAccent?: boolean;
	/** Tailwind text color class for the value (e.g., "text-red-600") */
	valueColor?: string;
	/** Tailwind text color class for the subtitle */
	subtitleColor?: string;
}

/* ColumnConfig moved to organisms/JobListMain with the extracted list body.
 * Re-exported here so existing page-level imports keep working unchanged. */
export type { ColumnConfig } from '../../organisms/JobListMain';

/** Configuration for a filter control in the filter panel */
export interface FilterControlConfig {
	/** Unique key for React rendering */
	key: string;
	/** Filter label text */
	label: string;
	/** Type of filter control */
	type: 'checkbox_group' | 'select' | 'date_range' | 'text' | 'chips' | 'tags';
	/** Options for checkbox_group, select, chips, or tags filters */
	options?: { value: string; label: string }[];
	/** Current value(s) -- string for single, string[] for multi */
	value: string | string[];
	/** Called when the value changes */
	onChange: (value: any) => void;
	/** Placeholder text for text/select inputs */
	placeholder?: string;
}

/** Props for the JobListTemplate */
export interface JobListTemplateProps {
	/** Page title (e.g., "Work Orders" or "Inspections") */
	title: string;
	/** Page subtitle describing the page purpose */
	subtitle: string;
	/** Create button label (e.g., "New Order" or "New Inspection") */
	createLabel?: string;
	/** Route to navigate to when the create button is clicked */
	createRoute?: string;
	/** Whether the current user can create new items */
	canCreate: boolean;
	/**
	 * Optional secondary action shown in the page header (e.g. "Generate PDF").
	 * Passed straight through to PageHeader's secondaryAction slot.
	 */
	secondaryActionLabel?: string;
	/** Click handler for the secondary action button */
	onSecondaryActionClick?: () => void;
	/** Disables the secondary action (e.g. while a PDF renders) */
	secondaryActionDisabled?: boolean;
	/** Stat card configurations -- one per card */
	stats: StatCardConfig[];
	/** Table column configurations */
	columns: ColumnConfig[];
	/** Filter control configurations for the filter panel */
	filters: FilterControlConfig[];
	/** The data items to display in the table */
	items: any[];
	/** Whether data is currently loading */
	isLoading: boolean;
	/** Error message to display, or null */
	error: string | null;
	/** Current page number (1-indexed) */
	currentPage: number;
	/** Total number of pages */
	totalPages: number;
	/** Total number of items across all pages */
	totalItems: number;
	/** Number of items per page */
	perPage: number;
	/** Called when the page changes */
	onPageChange: (page: number) => void;
	/** Called when retry is requested after an error */
	onRetry: () => void;
	/** Called to reset all filters */
	onResetFilters: () => void;
	/** Empty state message shown when no items match */
	emptyMessage?: string;
	/** Optional render function for a full-width summary row that
	 *	appears above each data row. The Inspections list uses this
	 *	to surface Building + Customer as a header band above the
	 *	per-row table cells. When omitted only the standard row renders. */
	renderRowHeader?: (item: any, index: number) => React.ReactNode;
	/** Optional extra classes merged onto the root element */
	className?: string;
	/**
	 * Overrides the root's data-ref marker. Per the atomic-design
	 * convention, a page that composes this template as its root passes
	 * its OWN identity here, so the rendered DOM reflects the real
	 * component rather than the shared wrapper.
	 */
	dataRef?: string;
}

/* ============================================
 * SKELETON COMPONENTS
 * Animated loading placeholders displayed while data loads.
 * ============================================ */

/** Skeleton for the stat summary cards row */
const StatCardSkeleton: React.FC = () => (
	<div className="rounded-12px p-20px bg-gray-100 animate-pulse h-112px" />
);

/* ============================================
 * FILTER PANEL SUB-COMPONENT
 * Renders the filter controls based on FilterControlConfig array.
 * ============================================ */

/** Renders all filter controls configured via the filters prop */
const FilterPanel: React.FC<{
	filters: FilterControlConfig[];
	onReset: () => void;
	onApply: () => void;
	/** Inside the mobile Modal: the Modal supplies the title and the
	 *  Reset / Show results footer, so the panel drops its own. */
	bare?: boolean;
}> = ({ filters, onReset, onApply, bare = false }) => (
	<div className="flex flex-col gap-20px">
		{/* Panel header with "FILTER CONTROL" title and RESET link */}
		{!bare && (
			<div className="flex items-center justify-between">
				<h3 className="text-14px font-heading uppercase tracking-wide text-brand-heading">FILTER CONTROL</h3>
				<button type="button" onClick={onReset} className="text-12px font-semibold uppercase tracking-wide text-brand-primary hover:text-brand-primary-hover transition-colors">RESET</button>
			</div>
		)}

		{/* Render each filter control based on its type */}
		{filters.map((filter) => (
			<div key={filter.key}>
				<label className="block text-12px font-semibold uppercase tracking-wide text-brand-text-muted mb-8px">{filter.label}</label>

				{/* Checkbox group filter -- multiple select via checkboxes */}
				{filter.type === 'checkbox_group' && filter.options && (
					<div className="flex flex-col gap-6px">
						{dedupeAndSort(filter.options).map((opt) => {
							const selected = Array.isArray(filter.value) ? filter.value : [];
							const isChecked = selected.includes(opt.value);
							return (
								<Checkbox key={opt.value} checked={isChecked} onChange={() => { const next = isChecked ? selected.filter((v) => v !== opt.value) : [...selected, opt.value]; filter.onChange(next); }} label={opt.label} className="job-list-template__filter-option items-center" dataRef="template-job-list-template-filter-option" />
							);
						})}
					</div>
				)}

				{/* Select dropdown filter */}
				{filter.type === 'select' && filter.options && (
					<select value={typeof filter.value === 'string' ? filter.value : ''} onChange={(e) => filter.onChange(e.target.value)} className="w-full px-12px py-8px min-h-44px desktop:min-h-36px text-16px desktop:text-14px border border-brand-border rounded-6px bg-white text-brand-text focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary">
						{dedupeAndSort(filter.options).map((opt) => (
							<option key={opt.value} value={opt.value}>{opt.label}</option>
						))}
					</select>
				)}

				{/* Date range filter -- two date inputs stacked */}
				{filter.type === 'date_range' && (
					<div className="flex flex-col gap-8px">
						<input type="date" value={Array.isArray(filter.value) ? filter.value[0] || '' : ''} onChange={(e) => { const val = Array.isArray(filter.value) ? filter.value : ['', '']; filter.onChange([e.target.value, val[1] || '']); }} placeholder="From" className="w-full px-12px py-8px min-h-44px desktop:min-h-36px text-16px desktop:text-14px border border-brand-border rounded-6px bg-white text-brand-text focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary" />
						<input type="date" value={Array.isArray(filter.value) ? filter.value[1] || '' : ''} onChange={(e) => { const val = Array.isArray(filter.value) ? filter.value : ['', '']; filter.onChange([val[0] || '', e.target.value]); }} placeholder="To" className="w-full px-12px py-8px min-h-44px desktop:min-h-36px text-16px desktop:text-14px border border-brand-border rounded-6px bg-white text-brand-text focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary" />
					</div>
				)}

				{/* Text search filter */}
				{filter.type === 'text' && (
					<input type="text" value={typeof filter.value === 'string' ? filter.value : ''} onChange={(e) => filter.onChange(e.target.value)} placeholder={filter.placeholder || 'Search...'} className="w-full px-12px py-8px text-16px border border-brand-border rounded-6px bg-white text-brand-text focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary placeholder:text-brand-text-subtle" />
				)}

				{/* Chip toggle buttons filter */}
				{filter.type === 'chips' && filter.options && (
					<div className="flex items-center gap-8px flex-wrap">
						{dedupeAndSort(filter.options).map((chip) => {
							const isActive = filter.value === chip.value;
							return (
								<button key={chip.value} type="button" onClick={() => filter.onChange(isActive ? '' : chip.value)} className={['job-list-template__chip', isActive ? 'job-list-template__chip--active' : '', 'inline-flex items-center px-12px py-6px min-h-44px desktop:min-h-0 rounded-full text-12px font-semibold uppercase tracking-wide transition-colors', isActive ? 'bg-brand-primary text-white' : 'bg-gray-100 text-brand-text-muted hover:bg-gray-200'].filter(Boolean).join(' ')}>
									{chip.label}
								</button>
							);
						})}
					</div>
				)}

				{/* Tag toggle buttons filter */}
				{filter.type === 'tags' && filter.options && (
					<div className="flex items-center gap-8px flex-wrap">
						{dedupeAndSort(filter.options).map((tag) => {
							const isActive = filter.value === tag.value;
							return (
								<button key={tag.value} type="button" onClick={() => filter.onChange(isActive ? '' : tag.value)} className={['job-list-template__tag', isActive ? 'job-list-template__tag--active' : '', 'inline-flex items-center px-12px py-6px min-h-44px desktop:min-h-0 rounded-6px text-12px font-semibold border transition-colors', isActive ? 'bg-brand-accent text-white border-brand-accent' : 'bg-white text-brand-text-muted border-brand-border hover:border-brand-accent hover:text-brand-accent'].filter(Boolean).join(' ')}>
									{tag.label}
								</button>
							);
						})}
					</div>
				)}
			</div>
		))}

		{/* Apply Parameters button */}
		{!bare && (
			<button type="button" onClick={onApply} className="w-full py-10px rounded-6px text-12px font-heading uppercase tracking-wide text-white transition-colors min-h-44px bg-brand-accent">APPLY PARAMETERS</button>
		)}
	</div>
);

/* ============================================
 * MAIN COMPONENT
 * ============================================ */

export const JobListTemplate: React.FC<JobListTemplateProps> = ({
	title,
	subtitle,
	createLabel,
	createRoute,
	canCreate,
	secondaryActionLabel,
	onSecondaryActionClick,
	secondaryActionDisabled,
	stats,
	columns,
	filters,
	items,
	isLoading,
	error,
	currentPage,
	totalPages,
	totalItems,
	perPage,
	onPageChange,
	onRetry,
	onResetFilters,
	emptyMessage = 'No items found.',
	renderRowHeader,
	className,
	dataRef = 'template-job-list-template',
}) => {
	const navigate = useNavigate();

	/* ---- Mobile filter panel visibility ---- */
	const [showMobileFilters, setShowMobileFilters] = useState(false);
	/* The pop-up is the below-desktop view only. If the window grows past the
	   breakpoint while it's open, the sidebar panel takes over and the pop-up
	   closes. (The Modal itself freezes the list behind it.) */
	const isDesktop = useIsDesktop();

	/** Applies filters (on mobile, also closes the filter overlay) */
	const applyFilters = () => {
		setShowMobileFilters(false);
	};

	/* ============================================
	 * RENDER
	 * ============================================ */

	return (
		<div className={['job-list-template', 'p-16px tablet:p-24px font-sans', className].filter(Boolean).join(' ')} data-ref={dataRef}>
			{/* Page header (PageHeader molecule) */}
			<PageHeader
				title={title}
				subtitle={subtitle}
				buttonLabel={canCreate ? createLabel : undefined}
				onButtonClick={canCreate && createRoute ? () => navigate(createRoute) : undefined}
				secondaryActionLabel={secondaryActionLabel}
				onSecondaryActionClick={onSecondaryActionClick}
				secondaryActionDisabled={secondaryActionDisabled}
			/>

			{/* Stat summary cards row */}
			<div className="job-list-template__stats grid grid-cols-2 desktop:grid-cols-4 gap-12px mb-24px">
				{isLoading ? (
					<>
						<StatCardSkeleton />
						<StatCardSkeleton />
						<StatCardSkeleton />
						<StatCardSkeleton />
					</>
				) : (
					stats.map((stat, idx) => (
						stat.isAccent ? (
							/* Accent card -- brand-accent background with white text */
							<div key={idx} className="rounded-12px p-20px shadow-sm relative overflow-hidden bg-brand-accent">
								<p className="text-12px font-heading uppercase tracking-wider text-white/70 mb-4px">{stat.label}</p>
								<p className="text-30px font-bold text-white">{stat.value}</p>
								<p className="text-12px text-white/60 mt-4px">{stat.subtitle}</p>
							</div>
						) : (
							/* Standard card -- white background with colored value */
							<div key={idx} className="bg-white rounded-12px border border-brand-border p-20px shadow-sm">
								<p className="text-12px font-heading uppercase tracking-wider text-brand-text-muted mb-4px">{stat.label}</p>
								<p className={`text-30px font-bold ${stat.valueColor || 'text-brand-accent'}`}>{stat.value}</p>
								<p className={`text-12px mt-4px ${stat.subtitleColor || 'text-brand-text-muted'}`}>{stat.subtitle}</p>
							</div>
						)
					))
				)}
			</div>

			{/* Main content area: Table (left) + Filter panel (right) */}
			<div className="job-list-template__body flex gap-24px items-start">
				{/* Data table (left side, flex-1 on desktop) — the shared
				    JobListMain organism owns the error/empty states, desktop
				    table, mobile card list, and both pagers. */}
				<JobListMain
					className="job-list-template__main flex-1 min-w-0"
					columns={columns}
					items={items}
					isLoading={isLoading}
					error={error}
					currentPage={currentPage}
					totalPages={totalPages}
					totalItems={totalItems}
					perPage={perPage}
					onPageChange={onPageChange}
					onRetry={onRetry}
					emptyMessage={emptyMessage}
					renderRowHeader={renderRowHeader}
				/>

				{/* Filter panel (right side, desktop only) */}
				<div className="job-list-template__sidebar hidden desktop:block w-288px flex-shrink-0">
					<div className="bg-white rounded-12px border border-brand-border shadow-sm p-20px sticky top-24px">
						<FilterPanel filters={filters} onReset={onResetFilters} onApply={applyFilters} />
					</div>
				</div>
			</div>

			{/* Mobile filter button (floating) */}
			<div className="desktop:hidden fixed bottom-80px tablet:bottom-24px right-16px z-30">
				<button type="button" onClick={() => setShowMobileFilters(true)} className="job-list-template__filter-fab inline-flex items-center gap-6px px-16px py-12px min-h-44px bg-brand-accent text-white rounded-full shadow-lg text-12px font-heading uppercase tracking-wide hover:bg-brand-accent-dark transition-colors">
					<svg className="w-14px h-14px" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
						<path strokeLinecap="round" strokeLinejoin="round" d="M10.5 6h9.75M10.5 6a1.5 1.5 0 1 1-3 0m3 0a1.5 1.5 0 1 0-3 0M3.75 6H7.5m3 12h9.75m-9.75 0a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m-3.75 0H7.5m9-6h3.75m-3.75 0a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m-9.75 0h9.75" />
					</svg>
					Filter
				</button>
			</div>

			{/* Mobile filter pop-up — the shared Modal, like every other
			    page's Filters pop-up (ResponsiveFilters): Escape and backdrop
			    close it, it stacks above the phone tab bar, and Reset / Show
			    results stay pinned while a long status list scrolls. */}
			<Modal
				isOpen={showMobileFilters && !isDesktop}
				onClose={applyFilters}
				title="Filters"
				size="md"
				className="job-list-template__filter-modal"
				dataRef="template-job-list-template-filter-modal"
				footer={(
					<div className="job-list-template__filter-actions flex flex-col-reverse tablet:flex-row tablet:justify-end gap-8px">
						<Button type="button" variant="secondary" size="sm" onClick={onResetFilters} className="w-full tablet:w-auto">Reset</Button>
						<Button type="button" variant="primary" size="sm" onClick={applyFilters} className="w-full tablet:w-auto">Show results</Button>
					</div>
				)}
			>
				<FilterPanel filters={filters} onReset={onResetFilters} onApply={applyFilters} bare />
			</Modal>
		</div>
	);
};

export default JobListTemplate;
